import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import PhoneInput from "react-phone-input-2";
import ErrorAlert from "~/components/shared/error_alert";
import { NOOP } from "~/constants/callbacks";
import LeadHelpers from "~/helpers/lead_helpers";
import { apiFetch } from "~/lib/fetchClient";

const LoadingSpinner = ({
  text = "Loading...",
  size = "lg",
  color = "grey",
}) => (
  <span className={`text-${color}`}>
    <FontAwesomeIcon
      icon={["far", "spinner"]}
      pulse
      size={size}
      className="mr-2"
    />
    {text}
  </span>
);

const SubscriptionView = ({
  title,
  message,
  buttonText,
  link = "/billing",
}) => (
  <div className="alert alert-success text-left">
    <h4 className="mt-2">{title}</h4>
    <p className="mt-3 mb-3">{message}</p>
    <a
      href={link}
      rel="noopener noreferrer"
      className="btn btn-success text-white"
    >
      {buttonText}
    </a>
  </div>
);

const TextMessageFormOnboarding = ({
  onSubscriptionSelect = NOOP,
  onPhoneSelect = NOOP,
}) => {
  const [view, setView] = useState("loading");
  const [errors, setErrors] = useState({});
  const [serviceNumber, setServiceNumber] = useState(null);
  const [forwardNumber, setForwardNumber] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [availablePhoneNumbers, setAvailablePhoneNumbers] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isMounted = useRef(true);
  const searchTimeout = useRef(null);

  useEffect(
    () => () => {
      isMounted.current = false;
      clearTimeout(searchTimeout.current);
    },
    [],
  );

  useEffect(() => {
    const initialize = async () => {
      const { subscription } = Rails;

      if (!subscription) {
        if (isMounted.current) {
          setView("missing_subscription");
        }
        return;
      }

      if (subscription.status !== "active" || !subscription.can_sms) {
        if (isMounted.current) {
          setView("onboard_subscription");
        }
        return;
      }

      onSubscriptionSelect(subscription);

      try {
        const myNumber = await apiFetch.get("/sms_numbers/mine");

        if (myNumber && myNumber.id) {
          onPhoneSelect(myNumber);
          return;
        }
        if (isMounted.current) {
          setView("onboard_phone_number");
        }
      } catch (error) {
        console.error("Error fetching existing phone number:", error);
      }
    };

    initialize();
  }, []);

  const handleSearch = useCallback(async (currentAreaCode) => {
    if (currentAreaCode.length !== 3) {
      return;
    }
    setIsSearching(true);
    setAvailablePhoneNumbers(null);

    try {
      const numbers = await apiFetch.get("/sms_numbers/search", {
        params: { code: currentAreaCode },
      });

      if (isMounted.current) {
        setAvailablePhoneNumbers(numbers);
      }
    } catch (err) {
      console.error("Error searching for phone numbers:", err);
      if (isMounted.current) {
        setAvailablePhoneNumbers([]);
      }
    } finally {
      if (isMounted.current) {
        setIsSearching(false);
      }
    }
  }, []);

  const handleAreaCodeChange = (e) => {
    const newAreaCode = e.target.value.slice(0, 3);
    setAreaCode(newAreaCode);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      handleSearch(newAreaCode);
    }, 500);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});
    try {
      const newPhoneNumber = await apiFetch.post("/sms_numbers", {
        json: {
          sms_number: {
            service_number: serviceNumber,
            forward_number: forwardNumber,
          },
        },
      });
      GlobalContainer.notify("BK Service Number registered");
      onPhoneSelect(newPhoneNumber);
    } catch (err) {
      console.error("Error submitting phone number:", err.errors);
      if (isMounted.current) {
        setErrors(err.errors);
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };

  const renderPhonePicker = () => (
    <div>
      <h4 className="mt-2 mb-3">Pick your phone number</h4>
      <p className="mb-3">
        First, select the area code of the phone number that will be assigned to
        you.
      </p>
      <div className="row ml-0 mr-0">
        <div className="col-sm-2 pl0 pr0">
          <input
            placeholder="Area code..."
            type="number"
            className="mr5 form-control form-control-sm"
            value={areaCode}
            onChange={handleAreaCodeChange}
          />
        </div>
      </div>

      <div className="mt-4">
        {isSearching && <LoadingSpinner text="Searching for numbers..." />}
        {availablePhoneNumbers && (
          <div>
            {availablePhoneNumbers.length === 0 ? (
              <span>No phone numbers found for this area code.</span>
            ) : (
              <div className="row">
                {availablePhoneNumbers.map((num) => (
                  <div className="col-md-4 mb-2" key={num}>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm btn-block mb5"
                      onClick={() => setServiceNumber(num)}
                    >
                      {LeadHelpers.renderPhoneNumber(num)}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-right mt-3">
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={() => handleSearch(areaCode)}
              >
                Search Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPhonePairForm = () => (
    <div>
      <h4 className="mt-2">Now add your mobile number</h4>
      <div className="row mt-3 mb-3 ml-0 mr-0">
        <div className="col-md-6 pl0 pr0-sm-down mb10-sm-down">
          <label htmlFor="forwardNumber" className="form-label">
            Texts will be forwarded to
          </label>
          <PhoneInput
            country="us"
            onlyCountries={["us"]}
            disableCountryCode
            disableDropdown
            specialLabel={false}
            placeholder="Enter your mobile number"
            value={LeadHelpers.nationalFormatPhoneNumber(forwardNumber)}
            onChange={setForwardNumber}
            inputProps={{
              id: "forwardNumber",
              name: "forwardNumber",
              className: classNames("form-control", {
                "has-error": !!errors.forward_number,
              }),
            }}
          />
        </div>
        <div className="col-md-6 pr0 pl0-sm-down">
          <label htmlFor="serviceNumber" className="form-label">
            Texts will come from
          </label>
          <div className="form-group has-feedback text-success mb5">
            <FontAwesomeIcon
              icon={["far", "fa-check"]}
              className="form-control-feedback form-control-feedback-right"
            />
            <div className="form-control text-success">
              {LeadHelpers.renderPhoneNumber(serviceNumber)}
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col-8">
          {Object.keys(errors).length > 0 && (
            <ErrorAlert
              heading="Oops, an error has occurred"
              message={errors}
            />
          )}
        </div>
        <div className="col-4 text-right">
          <button
            type="button"
            className="btn btn-secondary mr-2"
            onClick={() => {
              setServiceNumber(null);
              setErrors({});
            }}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <LoadingSpinner text="Saving..." size="sm" color="white" />
            ) : (
              "Start Texting"
            )}
          </button>
        </div>
      </div>
    </div>
  );

  switch (view) {
    case "loading":
      return <LoadingSpinner />;
    case "missing_subscription":
      return (
        <SubscriptionView
          title="Add billing info to set up Text Messaging"
          message="Before you can grab a phone number and start texting with Brokerkit, billing info needs to be added to your account."
          buttonText="Add Billing Info"
        />
      );
    case "onboard_subscription":
      return (
        <SubscriptionView
          title="Set up your Text Message subscription"
          message="How does it work? Just send a text here, replies will be forwarded to your mobile phone, and everything will be tracked in Brokerkit."
          buttonText="Change Subscription Plan"
        />
      );
    case "onboard_phone_number":
      return serviceNumber ? renderPhonePairForm() : renderPhonePicker();
    default:
      return null;
  }
};

TextMessageFormOnboarding.propTypes = {
  onSubscriptionSelect: PropTypes.func,
  onPhoneSelect: PropTypes.func,
};

export default TextMessageFormOnboarding;
